import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Zap, Play, Award, Book } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DonationSection } from '@/components/DonationSection';

const FeaturedLessons = () => {
  useSeoMeta({
    title: 'Lecciones Destacadas - Bitcoin Life Panamá',
    description: 'Lecciones populares para comenzar tu viaje en Bitcoin. Desde lo básico hasta temas avanzados.',
  });

  const featuredLessons = [
    {
      title: '¿Qué es Bitcoin?',
      description: 'Una introducción a la primera moneda digital descentralizada del mundo',
      duration: '15 min',
      level: 'Principiante',
      image: 'https://images.pexels.com/photos/844124/pexels-photo-844124.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Fundamentos', 'Historia', 'Dinero Digital'],
      learningOutcomes: [
        'Comprender qué es Bitcoin y cómo funciona',
        'Aprender sobre descentralización',
        'Conocer la diferencia con dinero tradicional',
        'Entender por qué Bitcoin es importante'
      ]
    },
    {
      title: 'Entendiendo Blockchain',
      description: 'Aprende cómo la tecnología blockchain impulsa Bitcoin',
      duration: '20 min',
      level: 'Principiante',
      image: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Tecnología', 'Blockchain', 'Seguridad'],
      learningOutcomes: [
        'Entender la estructura de blockchain',
        'Conocer cómo se validan transacciones',
        'Aprender sobre bloques y minería',
        'Comprender la inmutabilidad de datos'
      ]
    },
    {
      title: 'Minería de Bitcoin Explicada',
      description: 'Descubre cómo se crea nuevo Bitcoin y se asegura la red',
      duration: '25 min',
      level: 'Intermedio',
      image: 'https://images.pexels.com/photos/6771899/pexels-photo-6771899.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Minería', 'Prueba de Trabajo', 'Seguridad'],
      learningOutcomes: [
        'Entender el proceso de minería',
        'Conocer la Prueba de Trabajo (PoW)',
        'Aprender sobre dificultad y halving',
        'Comprender incentivos económicos'
      ]
    },
    {
      title: 'Inmersión Profunda en Lightning Network',
      description: 'Domina la solución de escalado de capa 2 de Bitcoin',
      duration: '30 min',
      level: 'Avanzado',
      image: 'https://images.pexels.com/photos/8370752/pexels-photo-8370752.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Lightning', 'Pagos', 'Escalabilidad'],
      learningOutcomes: [
        'Entender cómo funciona Lightning Network',
        'Aprender sobre canales de pago',
        'Conocer billeteras Lightning',
        'Practicar pagos instantáneos'
      ]
    },
    {
      title: 'Seguridad y Auto-Custodia',
      description: 'Aprende a proteger tu Bitcoin con las mejores prácticas',
      duration: '25 min',
      level: 'Intermedio',
      image: 'https://images.pexels.com/photos/6771607/pexels-photo-6771607.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Seguridad', 'Wallets', 'Privacidad'],
      learningOutcomes: [
        'Dominar claves públicas y privadas',
        'Usar billeteras de hardware',
        'Implementar multifirma',
        'Proteger tus fondos correctamente'
      ]
    },
    {
      title: 'Bitcoin en Panamá',
      description: 'Casos de uso reales y adopción en Ciudad de Panamá',
      duration: '20 min',
      level: 'Principiante',
      image: 'https://images.pexels.com/photos/3771118/pexels-photo-3771118.jpeg?auto=compress&cs=tinysrgb&w=800',
      topics: ['Adopción Local', 'Comercios', 'Economía Circular'],
      learningOutcomes: [
        'Conocer comercios que aceptan Bitcoin',
        'Aprender sobre la comunidad local',
        'Entender oportunidades en Panamá',
        'Participar en la economía circular'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-yellow-950/30 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTEsMTkxLDM2LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="px-4 py-1.5 text-sm bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 border-orange-200 dark:border-orange-800">
              <Award className="w-3.5 h-3.5 mr-1.5 inline" />
              Contenido Popular
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white">
              Lecciones Destacadas
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comienza tu viaje Bitcoin con estas lecciones cuidadosamente seleccionadas por nuestra comunidad
            </p>
          </div>
        </div>
      </div>

      {/* Featured Lessons Grid */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {featuredLessons.map((lesson, index) => (
              <Card key={index} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer border-2 hover:border-orange-200 dark:hover:border-orange-800">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={lesson.image}
                    alt={lesson.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Badge className="bg-white/90 text-gray-900 hover:bg-white">
                      {lesson.level}
                    </Badge>
                    <Badge className="bg-orange-500/90 text-white hover:bg-orange-500">
                      <Zap className="w-3 h-3 mr-1" />
                      {lesson.duration}
                    </Badge>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {lesson.title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {lesson.topics.map((topic, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs bg-white/20 text-white border-white/30">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                
                <CardHeader>
                  <CardDescription className="text-base">{lesson.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Lo que aprenderás:
                    </div>
                    <ul className="space-y-1.5">
                      {lesson.learningOutcomes.map((outcome, idx) => (
                        <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                          <span className="text-orange-500 mt-0.5">•</span>
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/lessons/beginner/1" className="w-full block">
                    <Button className="w-full group-hover:bg-orange-600 group-hover:text-white transition-colors">
                      <Play className="w-4 h-4 mr-2" />
                      Comenzar Lección
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <DonationSection />
      <Footer />
    </div>
  );
};

export default FeaturedLessons;
